/**
 * Tic-Tac-Logic Puzzle Generator
 *
 * Two-phase approach:
 * 1. Generate a complete valid grid
 * 2. Remove cells while maintaining unique solution
 */

import {
  type CellValue,
  type GridSize,
  type Difficulty,
  type GeneratorOptions,
  type GeneratedPuzzle,
  type GameState,
  EMPTY,
  X,
  O,
  CellFlags,
  seededRandom,
  shuffle,
} from './types';
import {
  createEmptyCells,
  setCell,
  getCell,
  cloneCells,
  countFilledCells,
  getIndex,
} from './grid';
import { isValidPlacement, isSolved } from './validation';
import { countSolutions } from './solver';

/**
 * Given count ranges by difficulty (percentage of total cells)
 */
const DIFFICULTY_RANGES: Record<Difficulty, { minPercent: number; maxPercent: number }> = {
  easy: { minPercent: 0.45, maxPercent: 0.55 },
  medium: { minPercent: 0.35, maxPercent: 0.45 },
  hard: { minPercent: 0.25, maxPercent: 0.35 },
  expert: { minPercent: 0.18, maxPercent: 0.28 },
};

/**
 * Get target given count range for difficulty and size
 */
function getGivenRange(difficulty: Difficulty, size: GridSize): { min: number; max: number } {
  const totalCells = size * size;
  const range = DIFFICULTY_RANGES[difficulty];
  return {
    min: Math.floor(totalCells * range.minPercent),
    max: Math.ceil(totalCells * range.maxPercent),
  };
}

/**
 * Generate a complete valid grid using backtracking
 * Uses row-by-row filling for better performance
 */
function generateCompleteGrid(size: GridSize, rng: () => number): CellValue[] | null {
  const cells = createEmptyCells(size);
  let iterations = 0;
  const maxIterations = size * size * 100;

  // Fill row by row (more efficient for constraint propagation)
  function fill(y: number, x: number): boolean {
    iterations++;
    if (iterations > maxIterations) {
      return false; // Timeout
    }

    // Move to next row if we've finished this one
    if (x >= size) {
      return fill(y + 1, 0);
    }

    // Done if we've filled all rows
    if (y >= size) {
      return true;
    }

    // Randomize symbol order for variety
    const symbols: CellValue[] = rng() < 0.5 ? [X, O] : [O, X];

    for (const symbol of symbols) {
      setCell(cells, size, x, y, symbol);

      if (isValidPlacement(cells, size, x, y)) {
        if (fill(y, x + 1)) {
          return true;
        }
      }

      // Backtrack
      setCell(cells, size, x, y, EMPTY);
    }

    return false;
  }

  if (fill(0, 0)) {
    return cells;
  }

  return null;
}

/**
 * Remove cells from complete grid while maintaining unique solution
 * Uses a more efficient approach with limited uniqueness checks
 */
function removeCells(
  solution: CellValue[],
  size: GridSize,
  difficulty: Difficulty,
  rng: () => number
): CellValue[] | null {
  const puzzle = cloneCells(solution);
  const range = getGivenRange(difficulty, size);
  const targetGivens = Math.floor((range.min + range.max) / 2);

  // Create list of all cell positions
  const positions: Array<{ x: number; y: number }> = [];
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      positions.push({ x, y });
    }
  }

  // Shuffle positions for random removal order
  const shuffledPositions = shuffle(positions, rng);

  // Track removed cells so we can restore if needed
  const removed: Array<{ idx: number; value: CellValue }> = [];
  let checksPerformed = 0;
  const maxChecks = Math.min(size * size, 30); // Limit expensive checks

  for (const { x, y } of shuffledPositions) {
    const idx = getIndex(size, x, y);
    const value = puzzle[idx]!;

    // Don't remove if already empty
    if (value === EMPTY) continue;

    // Check if we've reached target
    const currentGivens = countFilledCells(puzzle);
    if (currentGivens <= targetGivens) break;

    // Temporarily remove cell
    puzzle[idx] = EMPTY;
    removed.push({ idx, value });

    // Only do expensive uniqueness check periodically
    if (checksPerformed < maxChecks && removed.length % 3 === 0) {
      checksPerformed++;
      const solutions = countSolutions(puzzle, size, 2, 5000);

      if (solutions !== 1) {
        // Restore the last few removed cells until we have uniqueness
        while (removed.length > 0) {
          const last = removed.pop()!;
          puzzle[last.idx] = last.value;

          // Check again
          const check = countSolutions(puzzle, size, 2, 5000);
          if (check === 1) break;
        }
      }
    }
  }

  // Final uniqueness check
  const finalCheck = countSolutions(puzzle, size, 2, 10000);
  if (finalCheck !== 1) {
    // Restore cells until unique
    while (removed.length > 0 && countSolutions(puzzle, size, 2, 5000) !== 1) {
      const last = removed.pop()!;
      puzzle[last.idx] = last.value;
    }
  }

  return puzzle;
}

/**
 * Generate a puzzle with specified parameters
 */
export function generatePuzzle(options: GeneratorOptions): GeneratedPuzzle | null {
  const { size, difficulty, timeout = 5000 } = options;
  const startTime = Date.now();
  const maxAttempts = 50;
  let attempts = 0;

  while (attempts < maxAttempts) {
    if (Date.now() - startTime > timeout) {
      console.warn(`Generation timeout after ${attempts} attempts`);
      break;
    }

    attempts++;

    // Generate seed for this attempt
    const seed = options.seed ?? Date.now() + attempts;
    const rng = seededRandom(seed);

    // Phase 1: Generate complete grid
    const solution = generateCompleteGrid(size, rng);
    if (!solution) {
      continue;
    }

    // Verify solution is valid
    if (!isSolved(solution, size)) {
      continue;
    }

    // Phase 2: Remove cells
    const puzzle = removeCells(solution, size, difficulty, rng);
    if (!puzzle) {
      continue;
    }

    // Verify puzzle has unique solution
    const solutionCount = countSolutions(puzzle, size, 2);
    if (solutionCount !== 1) {
      continue;
    }

    // Extract givens
    const givens: Array<{ x: number; y: number; value: CellValue }> = [];
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const idx = getIndex(size, x, y);
        if (puzzle[idx] !== EMPTY) {
          givens.push({ x, y, value: puzzle[idx]! });
        }
      }
    }

    return {
      size,
      difficulty,
      givens,
      solution,
      givenCount: givens.length,
      seed,
    };
  }

  console.warn(`Failed to generate puzzle after ${maxAttempts} attempts`);
  return null;
}

/**
 * Create empty game state
 */
export function createEmptyState(size: GridSize, difficulty: Difficulty): GameState {
  return {
    size,
    difficulty,
    completed: false,
    cheated: false,
    cells: createEmptyCells(size),
    flags: new Array(size * size).fill(0),
    solution: [],
    selectedX: 0,
    selectedY: 0,
  };
}

/**
 * Create game state from generated puzzle
 */
export function createGameState(puzzle: GeneratedPuzzle): GameState {
  const { size, difficulty, givens, solution } = puzzle;
  const cells = createEmptyCells(size);
  const flags = new Array(size * size).fill(0);

  // Place givens
  for (const { x, y, value } of givens) {
    const idx = getIndex(size, x, y);
    cells[idx] = value;
    flags[idx] = CellFlags.IMMUTABLE;
  }

  return {
    size,
    difficulty,
    completed: false,
    cheated: false,
    cells,
    flags,
    solution,
    selectedX: 0,
    selectedY: 0,
  };
}

/**
 * Clone game state
 */
export function cloneState(state: GameState): GameState {
  return {
    ...state,
    cells: [...state.cells],
    flags: [...state.flags],
    solution: [...state.solution],
  };
}

/**
 * Generate a new game
 */
export function generateGame(size: GridSize, difficulty: Difficulty): GameState {
  const puzzle = generatePuzzle({ size, difficulty });

  if (!puzzle) {
    // Fallback: return empty state (shouldn't happen in practice)
    console.error('Failed to generate puzzle, returning empty state');
    return createEmptyState(size, difficulty);
  }

  return createGameState(puzzle);
}
